import { useMutation, useQuery } from "react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import type { SetStateAction } from "react";
import _ from "lodash";
import {
  getAllUser,
  bulkAssignGrantLetterTemplate,
  bulkUploadGrantLetterTemplates,
  deleteEmailTemplateAsset,
  deleteGrantLetterTemplate,
  getCCEmailSettings,
  getESignSettings,
  getEmailSettings,
  getEmailTemplateAssets,
  getEmailTemplateContent,
  getEmailTemplateNames,
  getEmailTemplateVariables,
  getEmployeePortalSettings,
  getEmployeeHistorySettings,
  getReportSettings,
  getGrantLetterSettings,
  getGrantLetterTemplates,
  listEmailTemplates,
  saveEmailTemplateContent,
  setCCEmailSettings,
  setDeleteDefaultSignature,
  setESginSettingDetails,
  setEmailSettingDetails,
  setEmployeePortalSettingDetails,
  setEmployeeHistorySettings,
  setReportSettings,
  setGrantLetterSettingDetails,
  uploadEmailTemplateAsset,
  uploadGrantLetterTemplateFiles,
} from "../api/siteSettings";
import { queryClient } from "./client";

export function useGetEmployeePortalSettings() {
  return useQuery({
    queryKey: "getEmployeePortalSettings",
    queryFn: getEmployeePortalSettings,
  });
}
export function useGetEmployeeHistorySettings() {
  return useQuery({
    queryKey: "getEmployeeHistorySettings",
    queryFn: getEmployeeHistorySettings,
  });
}
export function useGetEmailSettings() {
  return useQuery({
    queryKey: "getEmailSettings",
    queryFn: getEmailSettings,
    select: (data) => {
      const newData = _.omit(data, ["sendCCEmailsToAdmins"]);
      delete data.sendCCEmailsToAdmins;
      delete data.emailTemplateCC;
      return data;
    },
  });
}
export function useGetGrantLetterTemplates() {
  return useQuery({
    queryKey: "getGrantLetterTemplates",
    queryFn: getGrantLetterTemplates,
  });
}
export function useGetGrantLetterSettings() {
  return useQuery({
    queryKey: "getGrantLetterSettings",
    queryFn: getGrantLetterSettings,
  });
}
export function useESginSettings() {
  return useQuery({
    queryKey: "getESignSettings",
    queryFn: getESignSettings,
  });
}
export function useSetEmployeePortalSettings() {
  return useMutation({
    mutationKey: "setEmployeePortalSettingDetails",
    mutationFn: setEmployeePortalSettingDetails,
    onSuccess: () => {
      queryClient.invalidateQueries("getEmployeePortalSettings");
      queryClient.refetchQueries("getEmployeePortalSettings");
    },
  });
}
export function useSetEmployeeHistorySettings() {
  return useMutation({
    mutationKey: "setEmployeeHistorySettings",
    mutationFn: setEmployeeHistorySettings,
    onSuccess: () => {
      queryClient.invalidateQueries("getEmployeeHistorySettings");
    },
  });
}

export function useGetReportSettings() {
  return useQuery({
    queryKey: "getReportSettings",
    queryFn: getReportSettings,
  });
}

/**
 * Applies the company default when a report dialog opens, without replacing
 * the requester's choice after they have changed it for the current export.
 */
export function useReportPiiMaskDefault() {
  const { data: reportSettings } = useGetReportSettings();
  const [maskPiiData, setMaskPiiData] = useState(false);
  const hasRunTimeOverride = useRef(false);

  useEffect(() => {
    if (!hasRunTimeOverride.current && reportSettings) {
      setMaskPiiData(reportSettings.maskPiiByDefault);
    }
  }, [reportSettings]);

  const setMaskPiiDataWithOverride = useCallback(
    (nextValue: SetStateAction<boolean>) => {
      hasRunTimeOverride.current = true;
      setMaskPiiData(nextValue);
    },
    []
  );

  return { maskPiiData, setMaskPiiData: setMaskPiiDataWithOverride };
}

export function useSetReportSettings() {
  return useMutation({
    mutationKey: "setReportSettings",
    mutationFn: setReportSettings,
    onSuccess: () => {
      queryClient.invalidateQueries("getReportSettings");
    },
  });
}

export function useSetEmailSettings() {
  return useMutation({
    mutationKey: "setEmailSettingDetails",
    mutationFn: setEmailSettingDetails,
    onSuccess: () => {
      queryClient.invalidateQueries("getEmailSettings");
      queryClient.refetchQueries("getEmailSettings");
    },
  });
}
export function useSetGrantLetterSettings() {
  return useMutation({
    mutationKey: "setGrantLetterSettingDetails",
    mutationFn: setGrantLetterSettingDetails,
    onSuccess: () => {
      queryClient.invalidateQueries("getGrantLetterSettings");
      queryClient.refetchQueries("getGrantLetterSettings");
    },
  });
}
export function useDeleteDefaultSignature() {
  return useMutation({
    mutationFn: setDeleteDefaultSignature,
    mutationKey: "setDeleteDefaultSignature",
    onSuccess: () => {
      queryClient.invalidateQueries("getGrantLetterSettings");
      queryClient.refetchQueries("getGrantLetterSettings");
    },
  });
}

export function useSetESignSettings() {
  return useMutation({
    mutationKey: "setESginSettingDetails",
    mutationFn: setESginSettingDetails,
    onSuccess: () => {
      queryClient.invalidateQueries("getESignSettings");
      queryClient.refetchQueries("getESignSettings");
    },
  });
}
export function useGetEmailTemplateNames() {
  const excludeTemplates = [
    "EsopAmendment",
    "EsopRejected",
    "EsopActive",
    "EsopAmendmentReject",
    "EsopApproval",
  ];
  return useQuery({
    queryKey: "emailTemplateNames",
    queryFn: listEmailTemplates,
    select: (data) =>
      data.filter(
        (template) => !excludeTemplates.includes(template.templateName)
      ),
  });
}
export function useGetEmailTemplateContent(templateId: string) {
  return useQuery({
    queryKey: ["emailTemplate", templateId],
    queryFn: getEmailTemplateContent,
    enabled: false,
  });
}

export function useSaveEmailTemplate() {
  return useMutation({
    mutationKey: "saveEmailTemplate",
    mutationFn: saveEmailTemplateContent,
  });
}

export function useGetAllCompanyUsers() {
  return useQuery({
    queryKey: "allCompanyUsers",
    queryFn: getAllUser,
  });
}

export function useSaveEmailCCSettings() {
  return useMutation({
    mutationKey: "saveEmailCCSettings",
    mutationFn: setCCEmailSettings,
    onSuccess: () => {
      queryClient.invalidateQueries("getEmailCCSettings");
      queryClient.refetchQueries("getEmailCCSettings");
    },
  });
}

export function useGetEmailCCSettings() {
  return useQuery({
    queryKey: "getEmailCCSettings",
    queryFn: getCCEmailSettings,
  });
}

export function useBulkAssignGrantLetterTemplate() {
  return useMutation({
    mutationKey: "bulkAssignGrantLetterTemplate",
    mutationFn: bulkAssignGrantLetterTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries("getGrantLetterTemplates");
    },
  });
}

export function useBulkUploadGrantLetterTemplates() {
  return useMutation({
    mutationKey: "bulkUploadGrantLetterTemplates",
    mutationFn: bulkUploadGrantLetterTemplates,
  });
}

export function useDeleteGrantLetterTemplate() {
  return useMutation({
    mutationKey: "deleteGrantLetterTemplate",
    mutationFn: deleteGrantLetterTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries("getGrantLetterTemplates");
    },
  });
}

export function useUploadGrantLetterTemplateFiles() {
  return useMutation({
    mutationKey: "uploadGrantLetterTemplateFiles",
    mutationFn: uploadGrantLetterTemplateFiles,
    onSuccess: () => {
      queryClient.invalidateQueries("getGrantLetterTemplates");
      queryClient.refetchQueries("getGrantLetterTemplates");
    },
  });
}

export const EMAIL_TEMPLATE_ASSETS_KEY = "getEmailTemplateAssets";

export function useGetEmailTemplateAssets() {
  return useQuery({
    queryKey: EMAIL_TEMPLATE_ASSETS_KEY,
    queryFn: getEmailTemplateAssets,
  });
}

export function useGetEmailTemplateVariables(templateType: string) {
  return useQuery({
    queryKey: ["emailTemplateVariables", templateType],
    queryFn: () => getEmailTemplateVariables(templateType),
    enabled: !!templateType,
  });
}

export function useUploadEmailTemplateAsset() {
  return useMutation({
    mutationKey: "uploadEmailTemplateAsset",
    mutationFn: uploadEmailTemplateAsset,
    onSuccess: () => {
      queryClient.invalidateQueries(EMAIL_TEMPLATE_ASSETS_KEY);
    },
  });
}

export function useDeleteEmailTemplateAsset() {
  return useMutation({
    mutationKey: "deleteEmailTemplateAsset",
    mutationFn: deleteEmailTemplateAsset,
    onSuccess: () => {
      queryClient.invalidateQueries(EMAIL_TEMPLATE_ASSETS_KEY);
    },
  });
}
